import { AuthenticationGuard, useAuth } from 'auth';
import cc from 'classcat';
import ModalPortal from 'components/ModalPortal';
import OverlayPortal from 'components/OverlayPortal';
import { MapOverlayMachineProvider } from 'components/Overlays/MapOverlayMachine';
import AppStoreContext from 'contexts/AppStoreContext';
import { WebSocketProvider } from 'contexts/WebSocketContext';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import IAUploadDocPage from 'pages/internalAdmin/IAUploadDocPage';
import TrialBanner from 'pages/ProjectDashboard/TrialBanner';
import SubscriptionExpirationBanner from 'pages/ProjectDashboard/SubscriptionExpirationBanner';
import { AppGroupManagerProvider } from 'providers/GroupManagerProvider';
import { AppInteractionManagerProvider } from 'providers/InteractionManagerProvider';
import { ManageDrawingsSupportProvider } from 'providers/ManageDrawingsSupportProvider';
import { AppMapLayersManagerProvider } from 'providers/MapLayersManagerProvider';
import { AppProjectManagerProvider } from 'providers/ProjectManagerProvider';
import { AppRFIManagerProvider } from 'providers/RFIManagerProvider';
import { AppSheetManagerProvider } from 'providers/SheetManagerProvider';
import { AppUserInfoProvider } from 'providers/UserInfoProvider';
import React from 'react';
import { RouteComponentProps, Switch } from 'react-router-dom';
import { ToastContainer } from 'utils/toast';
import FreshStartSubscriptionBanner from 'pages/ProjectDashboard/FreshStartSubscriptionBanner';
import { isiPhone, isIpad, isAndroid } from 'constants/browserDetection';
import { EssentialsWebClient } from '../../essentials-web-client';
import ProjectRoutes from './ProjectRoutes';
import { Route } from './Route';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ArgoxWebClient } from '../../argox-web-client';

const AppRoutes: React.FC<RouteComponentProps> = (props) => {
  const { location, match } = props;
  const { actions, state: appState } = React.useContext(AppStoreContext);
  const [eulaLoaded, setEulaLoaded] = React.useState(false);
  const {
    isTrial,
    package: licensePackage,
    subscriptionExpiration,
  } = appState.userLicense;

  const { [FeatureFlag.ShowTrialBanner]: shouldShowTrialBanner } =
    useFeatureFlags();

  const { service } = useAnalytics();
  const { userId, email, displayName } = useAuth();

  const initApp = React.useCallback(async () => {
    await actions.acceptEula();
    await actions.getEula();
    await actions.getAndSetLicense();
    setEulaLoaded(true);
  }, []);

  React.useEffect(() => {
    initApp().catch(console.error);
  }, [initApp]);

  React.useEffect(() => {
    if (eulaLoaded && service.currentStateIs('active')) {
      service.identify(userId, { email, displayName });
    }
  }, [service.getCurrentState(), eulaLoaded, userId, email, displayName]);

  const onMapPage = location.pathname.includes('map');
  const onProjectDashboardPage = location.pathname.endsWith('/dashboard');

  // Don't show banners when the user is using Labs or Editors
  const isBannerAllowedOnPage =
    (!location.pathname.includes('editor') ||
      !location.pathname.includes('/labs') ||
      !onMapPage) &&
    (location.pathname.endsWith('my-workspace') ||
      location.pathname.endsWith('/projects') ||
      location.pathname.endsWith('/sessions') ||
      location.pathname.endsWith('/studio-projects') ||
      onProjectDashboardPage);

  const isAndroidAndIosDownloadLandingPage = isiPhone || isIpad || isAndroid;

  const showTrialBanner =
    isTrial && isBannerAllowedOnPage && !isAndroidAndIosDownloadLandingPage;

  const showSubscriptionExpiredBanner =
    !isTrial && isBannerAllowedOnPage && !isAndroidAndIosDownloadLandingPage; // Note: contains additional checks for showing the banner internally

  const showFreshStartSubscriptionBanner =
    shouldShowTrialBanner &&
    !isTrial &&
    licensePackage === 'Unpaid' &&
    subscriptionExpiration === '0001-01-01T00:00:00Z' && // extra condition to check if the user is net new..Comes from licensing service's query when licenseStub is N/A & represents a default value.
    isBannerAllowedOnPage &&
    !isAndroidAndIosDownloadLandingPage;

  const isOnLabsChildRoute =
    location.pathname.includes('/labs') &&
    location.pathname !== '/labs' &&
    !location.pathname.startsWith('/labs/search');

  const renderApp = () => (
    <div
      id="app"
      className={
        (cc({
          'file-view': match.url === '/project' && !onMapPage,
          'map-view': match.url === '/project' && onMapPage,
        }),
        'flex flex-col vw100 vh100')
      }
    >
      {showFreshStartSubscriptionBanner && <FreshStartSubscriptionBanner />}
      {showTrialBanner && <TrialBanner />}
      {showSubscriptionExpiredBanner && (
        <SubscriptionExpirationBanner onProjectPage={onProjectDashboardPage} />
      )}

      <Switch>
        <Route path="/project/:projectId" component={ProjectRoutes} />
        <Route path="/internal-admin/upload-doc" component={IAUploadDocPage} />
      </Switch>

      <div className="flex column h100 w100 grow-1">
        {!isOnLabsChildRoute ? <EssentialsWebClient /> : null}

        {isOnLabsChildRoute ? <ArgoxWebClient /> : null}
      </div>
      <ToastContainer />
    </div>
  );

  return eulaLoaded ? renderApp() : null;
};

const AppRoutesWrapper: React.VFC<RouteComponentProps> = (props) => {
  const { actions } = React.useContext(AppStoreContext);
  return (
    <AuthenticationGuard>
      <ManageDrawingsSupportProvider>
        <AppProjectManagerProvider>
          <AppUserInfoProvider>
            <AppRFIManagerProvider>
              <AppGroupManagerProvider>
                <AppSheetManagerProvider>
                  <AppInteractionManagerProvider>
                    <AppMapLayersManagerProvider>
                      <WebSocketProvider
                        onReconnect={actions.reregisterForEvents}
                      >
                        <MapOverlayMachineProvider>
                          <AppRoutes {...props} />
                          <OverlayPortal />
                          <ModalPortal name="main" />
                        </MapOverlayMachineProvider>
                      </WebSocketProvider>
                    </AppMapLayersManagerProvider>
                  </AppInteractionManagerProvider>
                </AppSheetManagerProvider>
              </AppGroupManagerProvider>
            </AppRFIManagerProvider>
          </AppUserInfoProvider>
        </AppProjectManagerProvider>
      </ManageDrawingsSupportProvider>
    </AuthenticationGuard>
  );
};

export default AppRoutesWrapper;
